import { messageStatus, type MessageActivity } from "@/lib/conversations";
import type { Appointment, MessageCadence, SendingWorkflow } from "@/lib/types";

/**
 * A contact's texts and appointments, as the contact record's SMS and Appointments tabs show them.
 *
 * Texts are core-api's (`GET /leads/{id}/sms`, ADR-095/098): every text with the lead in the
 * conversations the reader may open, which for a rep is not all of them. Appointments are core-api's
 * too (`GET /appointments?lead_id=`, ADR-085): every appointment with the lead that the reader may
 * see, which for a rep is the ones they host or booked. Both are records of their own, pointing at the
 * lead by id, never a copy of the contact.
 *
 * Pure, for `test/contact-activity.test.ts`.
 */

export type SmsDirection = "inbound" | "outbound";
export type SmsStatus = "scheduled" | "queued" | "sent" | "delivered" | "failed" | "received";

export interface ContactSms {
  id: string;
  lead_id: string;
  direction: SmsDirection;
  body: string;
  /** ISO timestamp. */
  sent_at: string;
  status: SmsStatus;
  /** Why it failed, as a sentence core-api writes for the carrier's code (ADR-098). */
  error: string | null;
  /** Our number on the text, and theirs. */
  from_number: string;
  to_number: string;
  /** Who on the team sent an outbound text; null for an automated one, and for inbound. */
  sent_by: { id: number; name: string } | null;
  /** The cadence step that sent it (ADR-135 in platform); null or absent on every other text. */
  sent_by_cadence?: MessageCadence | null;
  /** The workflow that sent it, for an automated one (ACL-385). Absent from an older core-api. */
  workflow?: SendingWorkflow | null;
  /** A scheduled text: when it goes out, the person's 8 am (ACL-630). */
  send_at?: string | null;
}

/** Oldest first, as a conversation reads. */
export function smsInOrder(list: readonly ContactSms[]): ContactSms[] {
  return [...list].sort((a, b) => a.sent_at.localeCompare(b.sent_at));
}

/** How many texts went each way, for the tab's summary line. One kept for the contact's 8 am has
 *  not gone yet (ACL-630), so it is counted apart from the ones sent. */
export function smsCounts(
  list: readonly ContactSms[],
): { inbound: number; outbound: number; failed: number; scheduled: number } {
  return {
    inbound: list.filter((m) => m.direction === "inbound").length,
    outbound: list.filter((m) => m.direction === "outbound" && m.status !== "scheduled").length,
    failed: list.filter((m) => m.status === "failed").length,
    scheduled: list.filter((m) => m.status === "scheduled").length,
  };
}

/** Which of our numbers a text was on: the one it left from, or the one it reached. */
export function ourNumber(m: Pick<ContactSms, "direction" | "from_number" | "to_number">): string {
  return m.direction === "outbound" ? m.from_number : m.to_number;
}

/** Our numbers the contact's texts were on, first used first: one per conversation they have with us. */
export function smsNumbers(list: readonly ContactSms[]): string[] {
  return [...new Set(smsInOrder(list).map(ourNumber))];
}

/** A text as a conversation's bubble, so the tab draws it as Conversations does (ACL-591): the time
 *  under it, the rest under the time's pointer (`messageDetail`). Who sent one of ours is a person,
 *  or nobody when it was automated; the workflow and the cadence are carried apart, for whoever may
 *  be told them. */
export function smsActivity(m: ContactSms): MessageActivity {
  const inbound = m.direction === "inbound";
  return {
    id: m.id,
    at: m.sent_at,
    kind: "message",
    channel: "sms",
    direction: m.direction,
    body: m.body,
    // A text is never opened on its own; only an email's line reads `GET /messages/{id}`.
    messageId: m.id,
    conversationId: null,
    subject: null,
    author: inbound || !m.sent_by ? null : { id: String(m.sent_by.id), name: m.sent_by.name },
    status: messageStatus(m.status, inbound),
    ourNumber: ourNumber(m),
    media: [],
    error: inbound ? null : m.error,
    cadence: inbound ? null : (m.sent_by_cadence ?? null),
    workflow: inbound ? null : (m.workflow ?? null),
    sendAt: inbound ? null : (m.send_at ?? null),
  };
}

/** Whether an appointment is still ahead: it has not ended, and it is still on (scheduled or
 *  confirmed). One that showed, was missed, was called off or was moved to another time is history,
 *  even when its time has not come. */
export function isUpcoming(a: Pick<Appointment, "ends_at" | "status">, now: number): boolean {
  return Date.parse(a.ends_at) >= now && (a.status === "scheduled" || a.status === "confirmed");
}

/** The tab's two lists: what is coming, soonest first, and what is done with, latest first. */
export function splitAppointments<T extends Pick<Appointment, "starts_at" | "ends_at" | "status">>(
  list: readonly T[],
  now: number,
): { upcoming: T[]; past: T[] } {
  const upcoming = list.filter((a) => isUpcoming(a, now)).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const past = list.filter((a) => !isUpcoming(a, now)).sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  return { upcoming, past };
}

/** "Mon, Sep 28 · 9:00 – 9:30 AM", or across two days in full. */
export function appointmentWhen(a: Pick<Appointment, "starts_at" | "ends_at">): string {
  const start = new Date(a.starts_at);
  const end = new Date(a.ends_at);
  const day = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const time = (d: Date) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (start.toDateString() === end.toDateString()) return `${day} · ${time(start)} – ${time(end)}`;
  return `${day} ${time(start)} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time(end)}`;
}

/** Minutes between start and end, for "30 min". */
export function appointmentMinutes(a: Pick<Appointment, "starts_at" | "ends_at">): number {
  return Math.max(0, Math.round((Date.parse(a.ends_at) - Date.parse(a.starts_at)) / 60_000));
}
